import { type Scale, addDays, dayDiff, formatDate, parseDate } from './scale';

export type DragMode = 'move' | 'resize-start' | 'resize-end';

export interface DragState {
  readonly taskId: string;
  readonly mode: DragMode;
  /** Toạ độ x của con trỏ lúc bắt đầu kéo. */
  readonly originX: number;
  readonly startDate: string;
  readonly dueDate: string;
}

export interface DateRange {
  readonly startDate: string;
  readonly dueDate: string;
}

/**
 * Ngày mới sau khi kéo `deltaX` pixel.
 *
 * Hàm thuần vì đây là chỗ dễ sai nhất của Gantt và cũng là chỗ khó bắt lỗi nhất
 * bằng mắt: sai một ngày ở biên thì thanh trông vẫn đúng, chỉ có con số trong
 * DB là lệch.
 *
 * Ba chế độ, ba luật khác nhau:
 *
 * - `move` — dịch cả hai đầu, giữ nguyên độ dài.
 * - `resize-start` — kéo mép trái; không cho vượt qua mép phải.
 * - `resize-end` — kéo mép phải; không cho lùi trước mép trái.
 *
 * Hai luật kẹp cuối đảm bảo thanh **luôn dài ít nhất một ngày**: server từ chối
 * `dueDate < startDate`, và một thao tác kéo thả bị từ chối giữa chừng là thao
 * tác người dùng không hiểu vì sao hỏng.
 */
export function applyDrag(drag: DragState, scale: Scale, deltaX: number): DateRange {
  const days = Math.round(deltaX / scale.dayWidth);
  const start = parseDate(drag.startDate);
  const due = parseDate(drag.dueDate);

  if (drag.mode === 'move') {
    return { startDate: formatDate(addDays(start, days)), dueDate: formatDate(addDays(due, days)) };
  }

  if (drag.mode === 'resize-start') {
    const length = dayDiff(start, due);
    // Kéo mép trái vượt qua mép phải ⇒ dừng lại ở đúng một ngày.
    const shift = Math.min(days, length);
    return { startDate: formatDate(addDays(start, shift)), dueDate: drag.dueDate };
  }

  const length = dayDiff(start, due);
  const shift = Math.max(days, -length);
  return { startDate: drag.startDate, dueDate: formatDate(addDays(due, shift)) };
}

/** Không gọi API khi người dùng chỉ click chứ không kéo. */
export function hasMoved(drag: DragState, next: DateRange): boolean {
  return next.startDate !== drag.startDate || next.dueDate !== drag.dueDate;
}
